import { call, put, select, spawn, takeEvery, takeLatest } from 'redux-saga/effects';

import Breakpoints from '@elab/core/utils/Breakpoints';
import logger from '@elab/core/utils/logger';
import { selectors } from '@elab/core/store/product';
import {
    dialogWithProductDetailsName,
    gwpChoicePopup,
    selectWishlistDialogName,
} from '@elab/core/utils/constants';
import {
    actions as uiActions,
    selectors as uiSelectors,
    types as uiTypes,
} from '@elab/core/store/ui';
import {
    actions as analyticsActions,
    selectors as analyticsSelectors,
} from '@elab/core/store/analytics';
import {
    loadProductVariantData,
    openPdpRtsPopup,
    updateProductPriceOnVariantSelection,
} from '@elab/core/store/product/saga';
import { actions as cartActions } from '@elab/core/store/cart';
import analytics from '@elab/core/utils/analytics';

import { types as globalSagaTypes } from '../global';
import * as api from '../product/api';
import { selectors as configSelectors } from '../config';

import { actions, types } from '.';

const storeName = 'minipdp';

export function* showProductDetails({ payload }) {
    yield call(loadProductVariantMatrix, payload);
    if (payload.eventLabel !== '') {
        yield put(analyticsActions.setCurrentPage({ page: payload.eventLabel }));
    }
    if (payload.productList !== '') {
        yield put(analyticsActions.setCurrentProductList({ productList: payload.productList }));
    }

    yield put(uiActions.openDialog({ name: 'product-details-popup' }));
    const windowSize = yield select(uiSelectors.getWindowSize);
    if (yield call(Breakpoints.isMobileOnly, windowSize.width)) {
        yield put(uiActions.scrollToTop({ target: '.e2-page' }));
    }
}

export function* handleAnalyticsViewOnOpenedPopup() {
    const product = yield select(selectors.getProductDetails, storeName);
    const collectStockInfo = yield select(configSelectors.collectStockInfo);

    const stockInfo = {};
    if (collectStockInfo) {
        stockInfo.inStock = product.stock && product.stock.stockLevelStatus === 'inStock';
        stockInfo.stockLevel = (product.stock && product.stock.stockLevel) || '';
    }

    const { pageType } = yield select(analyticsSelectors.getPageInfo);

    yield call(analytics.handleView, {
        event: 'genEcom',
        type: 'pdp-view',
        label: pageType,
        code: product.code,
        name: product.name,
        range: product.rangeName,
        price: product.price.value,
        brand: product.masterBrand && product.masterBrand.name,
        category: product.categoryNameHierarchy,
        variant: product.variantCategoryName,
        supplier: product.supplierDescription,
        ...stockInfo,
    });
    yield call(analytics.handleProductPopupOpened);
}

export function* showGwpProducts({ payload }) {
    yield put(uiActions.openDialog({ name: payload.dialogName }));
    const windowSize = yield select(uiSelectors.getWindowSize);
    if (yield call(Breakpoints.isMobileOnly, windowSize.width)) {
        yield put(uiActions.scrollToTop({ target: '.e2-page' }));
    }
}

export function* loadProductVariantMatrix(payload) {
    const productCode = payload.code;
    const loadedProductCode = yield select(selectors.getProductCode, storeName);
    if (loadedProductCode !== productCode) {
        const shouldSendVariantEventOnOpenedPopup = yield select(
            configSelectors.shouldSendVariantEventOnOpenedPopup,
        );
        yield put(actions.setLoading(true));
        try {
            const response = yield call(api.loadProductVariantMatrix, productCode);
            const useMarkDownPromotion = yield select(configSelectors.useMarkDownPromotionValue); // BU specific feature config to use markdown
            const updatedResponse = useMarkDownPromotion
                ? { ...response, useMarkDownPromotion }
                : response;
            yield put(actions.setProduct(updatedResponse));
            if (shouldSendVariantEventOnOpenedPopup) {
                yield spawn(handleAnalyticsViewOnOpenedPopup);
            }
        } catch (e) {
            yield spawn(logger.error, e);
        } finally {
            yield put(actions.setLoading(false));
        }
    }
}

export function* onProductDetailsDialogClose({ payload }) {
    const currentOpenedDialog = yield select(uiSelectors.getDialogName);
    if (
        (payload === dialogWithProductDetailsName &&
            currentOpenedDialog !== selectWishlistDialogName) ||
        payload === selectWishlistDialogName
    ) {
        yield put(actions.resetStore());
    }
    if (payload === gwpChoicePopup && payload === currentOpenedDialog) {
        yield put(cartActions.loadCart());
    }
    if (payload === currentOpenedDialog && currentOpenedDialog == 'product-details-popup') {
        yield call(resetAnalyticsData);
        yield call(analytics.handleProductPopupClosed);
    }
}

function* resetAnalyticsData() {
    yield put(analyticsActions.setCurrentPage({ page: null }));
    yield put(analyticsActions.setCurrentProductList({ productList: null }));
}

export default [
    takeLatest(uiTypes.CLOSE_DIALOG_BY_NAME, onProductDetailsDialogClose, {
        actions,
    }),
    takeLatest(types.SHOW_PRODUCT_DETAILS, showProductDetails),
    takeLatest(types.LOAD_PRODUCT_VARIANT_DATA, loadProductVariantData, {
        actions,
        selectors,
        types,
        storeName,
    }),
    takeLatest(types.UPDATE_PRODUCT_WITH_VARIANT_DATA, updateProductPriceOnVariantSelection, {
        actions,
        selectors,
        types,
        storeName,
    }),
    takeLatest(uiTypes.CLOSE_DIALOG_BY_NAME, onProductDetailsDialogClose),
    takeLatest(types.OPEN_PDP_RTS, openPdpRtsPopup, { selectors, storeName }),
    takeLatest(uiTypes.SHOW_GWP_ITEMS, showGwpProducts),
    takeEvery(globalSagaTypes.PAGE_LOAD, resetAnalyticsData),
];
